// ShareTutorialDialog - Share tutorial with candidates and link to multiple workspaces/courses
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Copy, Check, ExternalLink, Link2, Globe, BookOpen, Building2, Plus, X, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { workspaceAPI, courseAPI, tutorialAPI } from '@/lib/api';

export default function ShareTutorialDialog({ open, onOpenChange, page, userId, onUpdate }) {
  const [copied, setCopied] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [saving, setSaving] = useState(false);

  // Multi-workspace links
  const [links, setLinks] = useState([]);
  // Courses cache per workspace
  const [coursesCache, setCoursesCache] = useState({});

  const publicUrl = `${window.location.origin}/t/${page?.id}`;

  // Load workspaces + init links from page data
  useEffect(() => {
    if (open) {
      workspaceAPI.list().then(res => {
        setWorkspaces(res.data || []);
      }).catch(() => {});

      // Init links from page's workspace_links or legacy fields
      const existingLinks = page?.workspace_links || [];
      if (existingLinks.length > 0) {
        setLinks(existingLinks.map(l => ({ ...l })));
      } else if (page?.workspace_id) {
        setLinks([{ workspace_id: page.workspace_id, course_id: page.course_id || '' }]);
      } else {
        setLinks([]);
      }
    }
  }, [open, page?.id]);

  // Fetch courses for a workspace
  const fetchCourses = async (wsId) => {
    if (!wsId || coursesCache[wsId]) return;
    try {
      const res = await courseAPI.list(wsId);
      setCoursesCache(prev => ({ ...prev, [wsId]: res.data || [] }));
    } catch {
      setCoursesCache(prev => ({ ...prev, [wsId]: [] }));
    }
  };

  // Auto-fetch courses for all linked workspaces
  useEffect(() => {
    links.forEach(link => {
      if (link.workspace_id) fetchCourses(link.workspace_id);
    });
  }, [links]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      toast.success('Lien copié dans le presse-papier');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleOpenPublic = () => {
    window.open(publicUrl, '_blank');
  };

  const addLink = () => {
    setLinks(prev => [...prev, { workspace_id: '', course_id: '' }]);
  };

  const removeLink = (idx) => {
    setLinks(prev => prev.filter((_, i) => i !== idx));
  };

  const updateLink = (idx, field, value) => {
    setLinks(prev => prev.map((link, i) => {
      if (i !== idx) return link;
      if (field === 'workspace_id') {
        if (value) fetchCourses(value);
        return { workspace_id: value, course_id: '' };
      }
      return { ...link, [field]: value };
    }));
  };

  const handleSaveAssociation = async () => {
    if (!page?.id || !userId) return;
    setSaving(true);
    try {
      // Filter out empty links
      const validLinks = links.filter(l => l.workspace_id);
      // Set legacy fields from first link for backward compat
      const firstLink = validLinks[0] || {};
      await tutorialAPI.linkToCourse(page.id, {
        workspace_id: firstLink.workspace_id || null,
        course_id: firstLink.course_id || null,
        workspace_links: validLinks.length > 0 ? validLinks : null,
      }, userId);
      toast.success(`Association mise à jour (${validLinks.length} workspace${validLinks.length > 1 ? 's' : ''})`);
      if (onUpdate) onUpdate();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const isPublished = page?.status === 'published';
  const isVisible = page?.is_visible;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 size={18} />
            Partager le tutoriel
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Status check */}
          {(!isPublished || !isVisible) && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-sm">
              <p className="font-medium text-amber-800 dark:text-amber-200">⚠️ Tutoriel non accessible publiquement</p>
              <p className="text-amber-700 dark:text-amber-300 text-xs mt-1">
                {!isPublished && 'Le tutoriel est en brouillon. '}
                {!isVisible && 'Le tutoriel est masqué pour les candidats. '}
                Publiez-le et rendez-le visible pour le partager.
              </p>
            </div>
          )}

          {/* Public Link */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-1.5">
              <Globe size={14} className="text-primary" />
              Lien public
            </label>
            <div className="flex gap-2">
              <Input
                value={publicUrl}
                readOnly
                className="font-mono text-xs bg-muted/50"
                onClick={(e) => e.target.select()}
              />
              <Button variant="outline" size="icon" className="flex-shrink-0" onClick={handleCopyLink}>
                {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
              </Button>
              <Button variant="outline" size="icon" className="flex-shrink-0" onClick={handleOpenPublic}>
                <ExternalLink size={16} />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Les candidats peuvent accéder au tutoriel via ce lien
              {isPublished && isVisible
                ? ' (actif)'
                : ' (inactif — publiez et rendez visible)'}
            </p>
          </div>

          {/* Multi-Workspace / Course Association */}
          <div className="space-y-3 pt-2 border-t">
            <label className="text-sm font-medium flex items-center gap-1.5">
              <BookOpen size={14} className="text-primary" />
              Intégrer dans des cours
            </label>

            <div className="space-y-3 max-h-60 overflow-y-auto">
              {links.map((link, idx) => (
                <div key={idx} className="flex gap-2 items-start p-3 rounded-lg bg-muted/30 border border-border/50">
                  <div className="flex-1 space-y-2">
                    <div className="space-y-1">
                      <label className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Building2 size={10} /> Workspace
                      </label>
                      <Select
                        value={link.workspace_id || 'none'}
                        onValueChange={(v) => updateLink(idx, 'workspace_id', v === 'none' ? '' : v)}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sélectionner un workspace</SelectItem>
                          {workspaces.map(w => (
                            <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {link.workspace_id && (
                      <div className="space-y-1">
                        <label className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <BookOpen size={10} /> Cours
                        </label>
                        <Select
                          value={link.course_id || 'none'}
                          onValueChange={(v) => updateLink(idx, 'course_id', v === 'none' ? '' : v)}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Aucun cours</SelectItem>
                            {(coursesCache[link.workspace_id] || []).map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.icon || '📚'} {c.title || c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 mt-4 text-muted-foreground hover:text-red-500 flex-shrink-0"
                    onClick={() => removeLink(idx)}
                  >
                    <X size={14} />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5 text-xs"
              onClick={addLink}
            >
              <Plus size={14} />
              Ajouter un workspace
            </Button>

            {/* Current links summary */}
            {links.filter(l => l.workspace_id).length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <span>Lié à :</span>
                {links.filter(l => l.workspace_id).map((link, idx) => {
                  const ws = workspaces.find(w => w.id === link.workspace_id);
                  const courses = coursesCache[link.workspace_id] || [];
                  const course = courses.find(c => c.id === link.course_id);
                  return (
                    <Badge key={idx} variant="outline" className="text-[10px] gap-1">
                      {ws?.name || 'Workspace'}
                      {course && <> → {course.title || course.name}</>}
                    </Badge>
                  );
                })}
              </div>
            )}

            <Button
              onClick={handleSaveAssociation}
              disabled={saving}
              size="sm"
              className="w-full"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="animate-spin mr-1" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer l\'association'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
